import { NextPage } from 'next';
import styled from '@emotion/styled';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

// colors
import { primary, blue, gray, red, white, black } from '@styles/Colors';

// components
import Typography from '@components/_atoms/Typography';
import Title from '@components/_atoms/Title';

// 다국어 지원
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

// constants
import { TypoVariant } from '@constants/atoms';
import DefaultLayout from '@components/_templates/DefaultLayout';

const PolicyWrapper = styled.div`
  width: 100%;
  max-width: 1160px;
  padding: 110px 40px;
  margin: 0 auto;

  display: grid;
  row-gap: 32px;
`;

const TitleWrapper = styled.div`
  margin-bottom: 24px;
  text-align: center;
`;

const SectionWrapper = styled.div`
  display: grid;
  row-gap: 8px;
`;

const ImageWrapper = styled.div<{ height: string }>`
  width: 1080px;
  height: ${(props) => props.height};
  position: relative;
`;

const EnImageWrapper = styled.div`
  width: 1080px;
  height: 6536px;
  position: relative;
  margin: 104px auto;
`;

const Policy: NextPage = () => {
  const { i18n } = useTranslation('document');
  const ko = i18n.language === 'ko';

  return (
    <DefaultLayout>
      {ko ? (
        <PolicyWrapper>
          <TitleWrapper>
            <Title content="개인정보처리방침" />
          </TitleWrapper>
          <SectionWrapper>
            <Typography variant={TypoVariant.SH2} color={primary.gray}>
              고객의 개인정보를 위한 마켓오브메테리얼의 약속
            </Typography>
            <Typography variant={TypoVariant.B2} color={primary.gray}>
              본 페이지는 PC 환경에 최적화 되어 있습니다.
            </Typography>
          </SectionWrapper>
          <SectionWrapper>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              고객의 개인정보 보호는 마켓오브메테리얼 주식회사(이하 ‘마켓오브메테리얼’)의 중요한 사업 원칙입니다.
              마켓오브메테리얼은 지속적으로 고객에게 더 높은 수준의 서비스, 편리함과 가치를 제공하기 위해 노력하지만
              결코 고객 개인정보 보호라는 가치를 희생하며 서비스 목표를 달성하지 않습니다.
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              따라서, 마켓오브메테리얼이 수행하는 모든 활동은 정보통신망 이용촉진 및 정보보호 등에 관한 법률(이하
              ‘정보통신망법’)과 개인정보 보호법 등 국내 관련 법령을 준수하며, 본 개인정보 처리방침을 따릅니다.
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              본 개인정보 처리방침은 마켓오브메테리얼의 개인정보 처리와 관련한 정보를 제공하고 고객이 가진 권리 및
              어떻게 그 권리를 행사할 수 있는지에 대하여 설명합니다. 마지막으로, 마켓오브메테리얼의 서비스 이용 중
              개인정보와 관련하여 문의가 있을 경우 연락할 수 있는 개인정보 보호책임자 및 담당자의 연락처를 안내합니다.
            </Typography>
          </SectionWrapper>
          <SectionWrapper>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              마켓오브메테리얼은 고객의 개인정보와 관련된 변경사항이 생길 경우 개인정보 처리방침 개정을 통해 빠른 시일
              안에 고객에게 안내합니다. 개인정보 처리방침의 세부 목차는 아래와 같습니다.
            </Typography>
          </SectionWrapper>

          <hr />
          <SectionWrapper>
            <Typography variant={TypoVariant.SH2} color={primary.gray}>
              1. 수집하는 개인정보 및 이용에 관한 안내
            </Typography>

            <Typography variant={TypoVariant.B3} color={primary.gray}>
              1.1 비회원
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              비회원이란 마켓오브메테리얼 웹사이트를 통해 가입하지 않은 사용자 중 e-mail 등을 통해 거래진행을 요청한
              사용자를 말합니다.
            </Typography>
            <ImageWrapper height="200px">
              <Image src="/assets/images/policy0.png" alt="policy0" layout="fill" />
            </ImageWrapper>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              ※ 비회원 중 회원가입을 진행할 의향이 있는 사용자에 대해서 회원가입 편의성 및 거래의 연속성에 영향을 주는
              거래내역 관리를 위해 위 정보를 수집합니다.
            </Typography>

            <Typography variant={TypoVariant.B3} color={primary.gray}>
              1.2 정회원
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              정회원이란 마켓오브메테리얼 웹사이트를 통해 가입한 회원을 말합니다.
            </Typography>
            <ImageWrapper height="280px">
              <Image src="/assets/images/policy1.png" alt="policy1" layout="fill" />
            </ImageWrapper>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              ※ 부정이용이란 회원 탈퇴 후 재가입, 상품 구매 후 구매취소 등을 반복적으로 행하는 등 “마켓오브메테리얼”이
              제공하는 이벤트 혜택 등의 경제상 이익을 불·편법적으로 수취하는 행위, 이용약관 등에서 금지하고 있는 행위,
              명의도용 등의 불·편법 행위 등을 포함합니다.
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              ※ 고객의 권리를 보장해 드리기 위하여 탈퇴 회원 또는 장기 미이용 회원에게 환불 또는 리콜 안내를 목적으로
              구매정보를 이용하여 연락을 취할 수 있습니다.
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              ※ 위의 정보는 서비스 이용에 따른 통계∙분석에 이용될 수 있습니다.
            </Typography>

            <Typography variant={TypoVariant.B3} color={primary.gray}>
              1.2 이벤트 참여 회원
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              이벤트 참여 회원은 마켓오브메테리얼에서 운영하는 사이트의 게시판 등을 통하여 진행하는 이벤트에 참여한
              회원을 말합니다.
            </Typography>
            <ImageWrapper height="220px">
              <Image src="/assets/images/policy2.png" alt="policy2" layout="fill" />
            </ImageWrapper>
          </SectionWrapper>

          <hr />
          <SectionWrapper>
            <Typography variant={TypoVariant.SH2} color={primary.gray}>
              2. 개인정보의 제3자 제공
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              2.1 일반 이용 회원
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              주문과 결제가 이루어진 경우, 상담 및 배송 등의 원활한 거래 이행을 위하여 관련된 정보를 필요한 범위 내에서
              제3자에게 전달합니다. 제공받은 자의 자세한 정보는 아래의 ‘제공받는 자’의 각 항목을 클릭하시면 확인 할 수
              있습니다.
            </Typography>
            <ImageWrapper height="380px">
              <Image src="/assets/images/policy3.1.png" alt="policy3" layout="fill" />
            </ImageWrapper>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              ※ 동의 거부권 등에 대한 고지
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              개인정보 제공은 서비스 이용을 위해 꼭 필요합니다. 개인정보 제공을 거부하실 수 있으나, 이 경우 서비스
              이용이 제한될 수 있습니다. 개인정보 제3자 제공은 구매 시에만 이뤄지며, 명확한 내용은 구매 시 안내하여
              드립니다.
            </Typography>
          </SectionWrapper>

          <hr />
          <SectionWrapper>
            <Typography variant={TypoVariant.SH2} color={primary.gray}>
              3. 개인정보 처리위탁
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              마켓오브메테리얼은 위탁한 개인정보를 수탁자들이 안전하게 처리하고 있는지 지속적으로 관리 감독하고 있으며,
              수탁 업무가 종료된 때에 수탁자가 보유하고 있는 개인정보는 즉시 파기하도록 하고 있습니다.
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              3.1 개인정보 국내 처리 위탁 현황
            </Typography>
            <ImageWrapper height="200px">
              <Image src="/assets/images/policy4.png" alt="policy4" layout="fill" />
            </ImageWrapper>
          </SectionWrapper>
          <hr />
          <SectionWrapper>
            <Typography variant={TypoVariant.SH2} color={primary.gray}>
              4. 개인정보의 파기
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              수집된 개인정보의 보유•이용기간은 서비스 이용계약 체결(회원가입)시부터 서비스 이용계약 해지(탈퇴신청,
              직권탈퇴포함)시까지 입니다. 또한 동의 해지 시 고객의 개인정보를 상기 명시한 정보보유 사유에 따라 일정 기간
              저장하는 자료를 제외하고는 지체 없이 파기합니다. 종이에 출력된 개인정보는 분쇄기로 분쇄하거나 소각 등을
              통하여 파기하고, 전자적 파일형태로 저장된 개인정보는 기록을 재생할 수 없는 기술적 방법 또는 물리적 방법을
              사용하여 파기합니다.
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              수집•이용목적이 달성된 개인정보의 경우 별도의 DB에 옮겨져 내부규정 및 관련 법령을 준수하여 안전하게
              보관되며, 정해진 기간이 종료되었을 때 지체없이 파기됩니다. 이때, 별도의 DB로 옮겨진 개인정보는 회원이
              동의한 목적을 초과하거나 혹은 법률이 정한 경우 외의 다른 목적으로 이용되지 않습니다.
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              마켓오브메테리얼을 최종 이용 후 1년 동안 이용 기록이 없는 고객(장기미이용회원)의 개인정보는 별도로
              분리하여 안전하게 관리하게 되며, 대상자에게는 분리 보관 처리일을 기준으로 하여 최소 30일 이전에 이메일
              주소를 통해 안내를 합니다. 단, 통신비밀보호법, 전자상거래 등에서의 소비자보호에 관한 법률 등의 관계법령의
              규정에 의하여 보존할 필요가 있는 경우 규정된 기간 동안 고객의 개인정보를 보관합니다.
            </Typography>
          </SectionWrapper>
          <hr />
          <SectionWrapper>
            <Typography variant={TypoVariant.SH2} color={primary.gray}>
              5. 고객의 권리와 의무
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              5.1 고객의 권리
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              고객 및 법정대리인은 언제든지 수집 정보에 대하여 수정, 동의 철회, 삭제, 열람을 요청할 수 있습니다. 다만,
              동의 철회, 삭제 시 서비스의 일부 또는 전부 이용이 제한될 수 있습니다. 마켓오브메테리얼이 수집한 개인정보는
              아래와 같은 방법을 통해 확인할 수 있습니다
            </Typography>
          </SectionWrapper>
          <SectionWrapper>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              [수집된 개인정보 확인]
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              - 스틸보소 웹페이지에서 - 로그인 {'>'} My 정보 {'>'} 내 정보 관리
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              스틸보소 웹을 통해 직접 확인하지 못하는 정보는 마켓오브메테리얼 고객센터(1670-1621,
              support@marketofmaterial.com)에 요청하여 확인할 수 있습니다.
            </Typography>
          </SectionWrapper>
          <SectionWrapper>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              개인정보 동의 철회 및 삭제, 처리 정지를 요청하고자 하는 경우에는 마켓오브메테리얼 고객센터(1670-1621,
              support@marketofmaterial.com)를 통해 요청할 수 있습니다. 또한, 고객은 언제든 회원탈퇴를 통해 개인정보의
              수집 및 이용 동의를 철회할 수 있습니다. 이러한 요청 시, 서비스의 일부 또는 전부 이용이 제한될 수 있습니다.
              또한, 법률에 특별한 규정이 있거나 법령상 의무를 준수하기 위하여 불가피한 경우, 다른 사람의 생명·신체를
              해할 우려가 있거나 다른 사람의 재산과 그 밖의 이익을 부당하게 침해할 우려가 있는 경우, 개인정보를 처리하지
              아니하면 정보 주체와 약정한 서비스를 제공하지 못하는 등 계약의 이행이 곤란한 경우로서 정보주체가 그 계약의
              해지 의사를 명확하게 밝히지 아니한 경우에는 동의 철회, 삭제, 처리 정지가 어려울 수 있습니다.
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              요청하신 처리가 완료될 때까지 해당 정보를 이용하거나 타인에게 제공하지 않습니다. 또한, 합리적인 사유로
              잘못된 개인정보를 제3자에게 이미 제공한 경우, 그 결과를 지체 없이 제3자에게 통지하여 동의 철회, 삭제, 처리
              정지하도록 조치합니다.
            </Typography>
          </SectionWrapper>
          <SectionWrapper>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              5.2. 고객의 의무
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              고객은 자신의 개인정보를 보호할 의무가 있으며, 회사의 귀책사유가 없이 ID(이메일 주소), 비밀번호, 접근매체
              등의양도·대여·분실이나 로그인 상태에서 이석 등 고객 본인의 부주의나 관계법령에 의한 보안조치로 차단할 수
              없는 방법이나 기술을 사용한 해킹 등 회사가 상당한 주의에도 불구하고 통제할 수 없는 인터넷상의 문제 등으로
              개인정보가 유출되어 발생한 문제에 대해 회사는 책임을 지지 않습니다. 고객은 자신의 개인정보를 최신의 상태로
              유지해야 하며, 고객의 부정확한 정보 입력으로 발생하는 문제의 책임은 고객 자신에게 있습니다. 타인의
              개인정보를 도용한 회원가입 또는 ID등을 도용하여 결제 처리 시 고객 자격 상실과 함께 관계법령에 의거하여
              처벌될 수 있습니다. 고객은 아이디, 비밀번호 등에 대해 보안을 유지할 책임이 있으며 제3자에게 이를
              양도하거나 대여할 수 없습니다. 고객은 회사의 개인정보보호정책에 따라 보안을 위한 주기적인 활동에 협조할
              의무가 있습니다.
            </Typography>
          </SectionWrapper>
          <hr />
          <SectionWrapper>
            <Typography variant={TypoVariant.SH2} color={primary.gray}>
              6. 개인정보 보호책임자 및 담당자 안내
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              고객의 개인정보에 관한 업무를 총괄해서 책임지고, 개인정보와 관련된 불만처리 및 피해구제 등을 위하여 아래와
              같이 개인정보 보호책임자 및 담당부서를 지정하여 운영하고 있습니다. 회사가 제공하는 서비스를 이용하면서
              발생하는 모든 개인정보 보호 관련 문의, 불만, 피해구제 등에 관한 사항은 아래로 연락하여 문의할 수 있습니다.
              마켓오브메테리얼은 이러한 문의에 대해 지체 없이 답변 및 처리할 것입니다.
            </Typography>
          </SectionWrapper>
          <SectionWrapper>
            <div>
              <Typography variant={TypoVariant.SH3} color={primary.gray}>
                개인정보 보호책임자
              </Typography>
              <Typography variant={TypoVariant.B3} color={primary.gray}>
                성명 : 김경호
                <br />
                연락처 : +82 10-9270-3873
                <br />
                이메일 : support@marketofmaterial.com
              </Typography>
            </div>
            <div>
              <Typography variant={TypoVariant.SH3} color={primary.gray}>
                개인정보 민원처리 담당부서
              </Typography>
              <Typography variant={TypoVariant.B3} color={primary.gray}>
                부서명 : 마켓오브메테리얼 고객센터
                <br />
                연락처 : 1670-1621
                <br />
                이메일 : support@marketofmaterial.com
              </Typography>
            </div>
          </SectionWrapper>
          <SectionWrapper>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              기타 개인정보 침해 신고나 상담이 필요하신 경우에는 아래 기관에 문의 가능합니다.
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              - 개인정보 침해신고센터: (국번없이) 118 / (웹사이트: privacy.kisa.or.kr)
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              - 대검찰청 사이버범죄수사단 : (국번없이) 1301 / (웹사이트: www.spo.go.kr)
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              - 경찰청 사이버안전국 : (국번없이) 182 / (웹사이트: cyberbureau.police.go.kr)
            </Typography>
          </SectionWrapper>
          <hr />
          <SectionWrapper>
            <Typography variant={TypoVariant.SH2} color={primary.gray}>
              7. 고지의 의무
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              이 개인정보 처리방침은 시행일로부터 적용됩니다. 고객의 개인정보 권리에 중요한 변경 사유가 발생하는 경우
              최소 14일전에 마켓오브메테리얼 공지사항을 통하여 고지합니다. 이 외의 다른 변경사항이 발생하는 경우에는
              변경사항의 시행 최소 7일 전부터 공지사항을 통해 고지합니다.
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              개인정보 처리방침 버전 번호: 3.1
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              현재 개인정보 처리방침 공고일자: 2023년 2월 05일
            </Typography>
            <Typography variant={TypoVariant.B3} color={primary.gray}>
              현재 개인정보 처리방침 시행일자: 2023년 3월 05일
            </Typography>
          </SectionWrapper>
        </PolicyWrapper>
      ) : (
        <EnImageWrapper>
          <Image src="/assets/images/policy-en.1.png" alt="policy-en" layout="fill" />
        </EnImageWrapper>
      )}
    </DefaultLayout>
  );
};

export const getStaticProps = async ({ locale }: { locale: any }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['document', 'common', 'component'])),
  },
});

export default Policy;
